import {
  Greenhouse,
  PaginationData,
} from 'constants/interface';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateGreenhouseData extends Omit<
  Greenhouse,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy' |
  'organization'
> {
  organization: 'ORG1' | 'ORG2'
}

export interface UpdateGreenhouseData {
  id: string
  data: CreateGreenhouseData
}

export interface GreenhouseApi {
  createGreenhouse(greenhouseData: CreateGreenhouseData): Promise<Greenhouse>
  updateGreenhouse(greenhouseData: UpdateGreenhouseData): Promise<Greenhouse>
  getGreenhouse(greenhouseId: string): Promise<Greenhouse>
  getGreenhouseList(filter: PaginationData): Promise<PaginatedResponse<Greenhouse>>
}

const greenhouseApiBuilder = (): GreenhouseApi => {
  const createGreenhouse = async (
    greenhouseData: CreateGreenhouseData,
  ): Promise<Greenhouse> => {
    const params: Record<string, unknown> = {
      ...greenhouseData,
    };
    const result = await requestClient.post('/greenhouses', params);

    const createdGreenhouse: Greenhouse = result.data as Greenhouse;
    return createdGreenhouse;
  };

  const updateGreenhouse = async (
    greenhouseData: UpdateGreenhouseData,
  ): Promise<Greenhouse> => {
    const { id: greenhouseId, data } = greenhouseData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/greenhouses/${greenhouseId}`, params);
    const updatedGreenhouse: Greenhouse = result.data as Greenhouse;
    return updatedGreenhouse;
  };

  const getGreenhouse = async (
    greenhouseId: string,
  ): Promise<Greenhouse> => {
    const result = await requestClient.get(`/greenhouses/${greenhouseId}`);
    const greenhouse: Greenhouse = result.data as Greenhouse;
    return greenhouse;
  };

  const getGreenhouseList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<Greenhouse>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/greenhouses', params);
    const pagination = getPaginationHeaders(result);
    const greenhouseList: Greenhouse[] = result.data as Greenhouse[];
    return {
      pagination,
      data: greenhouseList,
    };
  };

  return {
    createGreenhouse,
    updateGreenhouse,
    getGreenhouse,
    getGreenhouseList,
  };
};

export const greenhouseApi = greenhouseApiBuilder();
